import { useQuery, useQueryClient } from '@tanstack/react-query'
import AppDialog from '../app_dialog'
import { TextField, Badge, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import { LockSimple } from '@phosphor-icons/react'
import RowMenu from '../row_menu'
import useStore from '../../hooks/use_store'
import { useNavigate } from 'react-router-dom'
import useTableFilters from '../../hooks/use_table_filters'
import { useState } from 'react'

const SchemasTable = ({ queryKey }) => {
  const { createModel, deleteModel } = useStore()
  const { data: airlines, error: airlinesError, isLoading: airlinesLoading } = useQuery(['api/airlines-list'])

  const filteringOptions = [
    {
      label: 'State',
      key: 'state',
      type: 'checkboxes',
      options: [
        { label: 'Locked', key: 'locked' },
        { label: 'Unlocked', key: 'unlocked' },
      ],
    },
    {
      label: 'Airline',
      key: 'airline',
      type: 'checkboxes',
      options: airlines?.map((airline) => ({ label: airline.name, key: airline.id })),
    },
  ]
  const { queryString, searching, filtering, pagination } = useTableFilters({
    filteringOptions,
    enableSearch: true,
  })
  const { data: schemas, error, isLoading, refetch } = useQuery([queryKey, queryString])
  const navigate = useNavigate()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [originSchemaID, setOriginSchemaID] = useState('')
  const [newSchemaName, setNewSchemaName] = useState('')
  const queryClient = useQueryClient()

  const renderAirlinesTags = (airlines) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {airlines.map((airline) => (
          <Badge key={airline}>{airline}</Badge>
        ))}
      </div>
    )
  }

  const columns = [
    { key: 'name', header: 'Name', render: (data) => data.name },
    { key: 'airline_name', header: 'Airlines', render: (data) => renderAirlinesTags(data.airlines) },
    { key: 'cycles', header: 'Cycles', render: (data) => data.cycles },
    {
      key: 'locked',
      header: 'Locked',
      render: (data) => (data.is_locked ? <LockSimple size={20} aria-label='Locked' /> : ''),
    },
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.name}`}
          items={[
            {
              id: 'copy-schema',
              label: 'Copy Schema',
              onSelect: () => {
                setOriginSchemaID(data.id)
                setNewSchemaName(data.name + ' (copy)')
                setIsOpenModal(true)
              },
            },
            {
              id: 'delete',
              label: 'Delete',
              tone: data.is_locked ? 'default' : 'danger',
              onSelect: () =>
                deleteModel.mutate(
                  {
                    endpoint: 'schemas',
                    id: data.id,
                    deleteNoun: 'schema',
                    deleteName: data.name,
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries([queryKey, queryString])
                    },
                  }
                ),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <div>
      <DataTable
        caption='Schemas'
        columns={columns}
        rows={schemas?.data ?? []}
        rowKey={(data) => String(data.id)}
        onRowClick={(data) => navigate(`/settings/schemas/${data.id}`)}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No schemas found!' detail='Clone an existing schema to begin.' />}
        searching={searching}
        filtering={filtering}
        pagination={pagination}
        totalRows={schemas?.meta?.total ?? 0}
      />
      {isOpenModal && (
        <AppDialog
          title='Copy schema'
          onClose={() => setIsOpenModal(false)}
          confirmText='Save'
          onConfirm={() => {
            createModel.mutate(
              {
                endpoint: 'schemas',
                data: { schemaID: originSchemaID, name: newSchemaName },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries([queryKey, queryString])
                },
              }
            )
            setIsOpenModal(false)
          }}
        >
          <TextField
            label='Schema Name'
            type='text'
            value={newSchemaName}
            onChange={(e) => setNewSchemaName(e.target.value)}
          />
        </AppDialog>
      )}
    </div>
  )
}

export default SchemasTable
